import {
  all,
  takeEvery,
  put,
  call,
  select,
  takeLatest,
} from "redux-saga/effects";
import Tinycon from "tinycon";

import { getProfileId } from "../../selectors/chat/application";
import {
  getUnreadChatsCount,
  isMuted,
  getChatProfile,
} from "../../selectors/chat/chat";
import { get, isEmpty, size } from "lodash";

const notify = ({ title, ...options }) =>
  new Promise((resolve, reject) => {
    const notification = new Notification(title, options);

    const notificationCloser = () => {
      notification.close();
    };
    window.addEventListener("beforeunload", notificationCloser);

    notification.onclick = () => {
      window.focus();
      resolve();
      notification.close();
    };
    notification.onclose = () => {
      console.log("CLOSED", title, options);
      window.removeEventListener("beforeunload", notificationCloser);
      reject();
    };
  });

function* notifyForIncomingMessage({ payload }) {
  const membershipId = yield select(getProfileId);

  // Create notification only for messages from other people
  if (membershipId === payload.from.id) {
    return;
  }

  const _isMuted = yield select(isMuted, {
    chatId: payload.chat_id,
  });

  if (_isMuted) {
    return;
  }

  const profile = yield select(getChatProfile, { chatId: payload.chat_id });

  try {
    yield call(notify, {
      title: profile.name,
      icon: profile.imageUrl,
      body: payload.text
        ? profile.type === "group"
          ? `${payload.from.name}: ${payload.text}`
          : payload.text
        : chatAttachmentPreview(payload),
      tag: payload.chat_id,
    });

    yield put({
      type: "chat/NAVIGATE",
      payload: {
        route: "showChat",
        params: { chatId: payload.chat_id },
      },
    });
  } catch (e) {
    // Notification was closed
  }
}

function* setTinycon() {
  const unreadChats = yield select(getUnreadChatsCount);

  Tinycon.setOptions({
    color: "#FFFFFF",
    background: "#0081ce",
    fallback: true,
  });
  Tinycon.setBubble(unreadChats > 99 ? "+" : unreadChats);
}

function* notificationsSaga() {
  if (window.Notification) {
    yield takeEvery("chat/MESSAGE/RECEIVE", notifyForIncomingMessage);
  }

  yield all([
    call(setTinycon),
    takeLatest(
      [
        "chat/MY_CHATS/REPLY",
        "chat/MESSAGE/RECEIVE",
        "chat/MARK_AS_READ",
        "chat/MARK_AS_READ/RECEIVE",
      ],
      setTinycon,
    ),
  ]);
}

function chatAttachmentPreview(entity) {
  const fileCount = size(get(entity, "files", []));
  const imageCount = size(get(entity, "images", []));

  if (fileCount === 0 && imageCount === 0) {
    return null;
  }

  const isGroupChat = !isEmpty(get(entity, "group_name"));
  const params = {
    author: get(entity, "from.name", ""),
  };

  let i18nKey = "js.chat.attachment_preview.";
  i18nKey += isGroupChat ? "group_chat." : "direct_chat.";

  if (fileCount > 0 && imageCount > 0) {
    // files and images
    if (fileCount === 1 && imageCount === 1) {
      i18nKey += "one_file_one_image";
    } else if (fileCount === 1 && imageCount > 1) {
      i18nKey += "one_file_multiple_images";
      params.count = imageCount;
    } else if (fileCount > 1 && imageCount === 1) {
      i18nKey += "multiple_files_one_image";
      params.count = fileCount;
    } else {
      i18nKey += "multiple_files_multiple_images";
      params.files_count = fileCount;
      params.images_count = imageCount;
    }
  } else if (fileCount > 0) {
    // only files
    if (fileCount === 1) {
      i18nKey += "one_file";
    } else {
      i18nKey += "multiple_files";
      params.count = fileCount;
    }
  } else if (imageCount > 0) {
    // only images
    if (imageCount === 1) {
      i18nKey += "one_image";
    } else {
      i18nKey += "multiple_images";
      params.count = imageCount;
    }
  }

  return I18n.t(i18nKey, params);
}

export default notificationsSaga;
